import {useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Bell, Check, Search, Star} from "lucide-react";
import {useLocation} from "react-router-dom";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {DataState} from "../../shared/components/DataState";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatDateTime} from "../../shared/utils/format";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {acceptNotification, cancelReview, loadDeployerInbox, loadNotificationOperations, loadReviews} from "./api";
import {
    canAcceptNotification,
    filterReviews,
    flattenReviews,
    normalizeNotificationOperations,
    notificationStatusTone,
} from "./contracts";
import {ReviewCancelDialog} from "./components/ReviewCancelDialog";
import "./inbox.css";
import {useTranslation, i18n} from "../../shared/i18n";


const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const InboxHeader = ({reviews, deployer}) => (
    <header className="admin-page-header">
        <div>
            <h1 className="admin-page-title">
                {reviews ? i18n.t("sections.inbox.p1") : deployer ? i18n.t("sections.inbox.p72") : i18n.t("sections.inbox.p2")}
            </h1>
            <p className="admin-page-copy">
                {reviews ? i18n.t("sections.inbox.p3") : deployer ? i18n.t("sections.inbox.p73") : i18n.t("sections.inbox.p4")}
            </p>
        </div>
        <div className="admin-pill">{reviews ? <Star size={14} /> : <Bell size={14} />} {reviews ? "Reviews" : "Operations"}</div>
    </header>
);

const NotificationQueue = ({deployer}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [result, setResult] = useState(null);
    const query = useQuery({
        queryKey: ["admin-inbox", deployer ? "deployer" : "notifications"],
        queryFn: deployer ? loadDeployerInbox : loadNotificationOperations,
    });
    const data = normalizeNotificationOperations(query.data);
    const acceptMutation = useMutation({
        mutationFn: acceptNotification,
        onMutate: () => setResult(null),
        onSuccess: () => {
            setResult({kind: "success", message: t("sections.inbox.p5")});
            void queryClient.invalidateQueries({queryKey: ["admin-inbox", "notifications"]});
        },
        onError: (error) => setResult({kind: "error", message: errorMessage(error, t("sections.inbox.p6"))}),
    });

    return (
        <>
            <p className="inbox-scope-note">{deployer ? t("sections.inbox.p74") : t("sections.inbox.p7")}</p>
            <section className="inbox-kpi-strip" aria-label={t("sections.inbox.p8")}>
                <article><span>{t("sections.inbox.p9")}</span><strong>{data.notifications.length}</strong><small>{t("sections.inbox.p10")}</small></article>
                <article><span>{t("sections.inbox.p11")}</span><strong>{data.pendingCount}</strong><small>{data.pendingCount} {t("sections.inbox.p12")}</small></article>
                <article><span>{t("sections.inbox.p13")}</span><strong>{data.changedCount}</strong><small>{t("sections.inbox.p14")}</small></article>
                <article><span>{t("sections.inbox.p15")}</span><strong>{data.rejectedCount}</strong><small>{t("sections.inbox.p16")}</small></article>
            </section>
            {result ? <p className={`inbox-result ${result.kind === "error" ? "is-error" : ""}`} role={result.kind === "error" ? "alert" : "status"}>{result.message}</p> : null}
            {query.isError ? (
                <DataState title={t("sections.inbox.p17")} copy={errorMessage(query.error, t("sections.inbox.p18"))} />
            ) : query.isLoading ? (
                <p className="inbox-loading">{t("sections.inbox.p19")}</p>
            ) : data.notifications.length === 0 ? (
                <DataState title={t("sections.inbox.p20")} copy={t("sections.inbox.p21")} />
            ) : (
                <div className="inbox-notification-list">
                    {data.notifications.map((item) => {
                        const actionable = canAcceptNotification(item, data.actions);
                        const accepting = acceptMutation.isPending && acceptMutation.variables === item.id;
                        return (
                            <article className="inbox-notification" key={item.id ?? `${item.title}-${item.text}`}>
                                <div className="inbox-notification-copy">
                                    <div><strong>{item.title}</strong><StatusBadge tone={notificationStatusTone(item.status)}>{item.status}</StatusBadge></div>
                                    <p>{item.text || t("sections.inbox.p22")}</p>
                                </div>
                                {actionable ? (
                                    <button className="inbox-primary-button" type="button" aria-label={t("sections.inbox.p23", {v0: item.id})} disabled={accepting} onClick={() => acceptMutation.mutate(item.id)}>
                                        <Check size={16} /> {accepting ? t("sections.inbox.p24") : t("sections.inbox.p25")}
                                    </button>
                                ) : null}
                            </article>
                        );
                    })}
                </div>
            )}
        </>
    );
};

const ReviewQueue = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [filters, setFilters] = useState({search: "", type: "all", status: "all"});
    const [selectedReview, setSelectedReview] = useState(null);
    const [result, setResult] = useState(null);
    const query = useQuery({queryKey: ["admin-inbox", "reviews"], queryFn: loadReviews});
    const rows = useMemo(() => flattenReviews(query.data), [query.data]);
    const filteredRows = useMemo(() => filterReviews(rows, filters), [rows, filters]);
    const cancelMutation = useMutation({
        mutationFn: cancelReview,
        onMutate: () => setResult(null),
        onSuccess: () => {
            setSelectedReview(null);
            setResult({kind: "success", message: t("sections.inbox.p26")});
            void queryClient.invalidateQueries({queryKey: ["admin-inbox", "reviews"]});
        },
    });
    const activeCount = rows.filter((item) => !item.is_canceled).length;

    return (
        <>
            <p className="inbox-scope-note">{t("sections.inbox.p27")}</p>
            <section className="inbox-kpi-strip" aria-label={t("sections.inbox.p28")}>
                <article><span>{t("sections.inbox.p9")}</span><strong>{rows.length}</strong><small>{t("sections.inbox.p29")}</small></article>
                <article><span>{t("sections.inbox.p30")}</span><strong>{activeCount}</strong><small>{t("sections.inbox.p31")}</small></article>
                <article><span>{t("sections.inbox.p32")}</span><strong>{rows.length - activeCount}</strong><small>{t("sections.inbox.p33")}</small></article>
            </section>
            <section className="inbox-filters" aria-label={t("sections.inbox.p34")}>
                <label><span>{t("sections.inbox.p35")}</span><div className="inbox-search"><Search size={15} /><input aria-label={t("sections.inbox.p36")} value={filters.search} onChange={(event) => setFilters({...filters, search: event.target.value})} placeholder={t("sections.inbox.p37")} /></div></label>
                <label><span>{t("sections.inbox.p38")}</span><select aria-label={t("sections.inbox.p39")} value={filters.type} onChange={(event) => setFilters({...filters, type: event.target.value})}><option value="all">{t("sections.inbox.p40")}</option><option value="branch">{t("sections.inbox.p41")}</option><option value="personal">{t("sections.inbox.p42")}</option></select></label>
                <label><span>{t("sections.inbox.p43")}</span><select aria-label={t("sections.inbox.p44")} value={filters.status} onChange={(event) => setFilters({...filters, status: event.target.value})}><option value="all">{t("sections.inbox.p40")}</option><option value="active">{t("sections.inbox.p30")}</option><option value="canceled">{t("sections.inbox.p32")}</option></select></label>
            </section>
            {result ? <p className="inbox-result" role="status">{result.message}</p> : null}
            {query.isError ? (
                <DataState title={t("sections.inbox.p45")} copy={errorMessage(query.error, t("sections.inbox.p18"))} />
            ) : query.isLoading ? (
                <p className="inbox-loading">{t("sections.inbox.p46")}</p>
            ) : rows.length === 0 ? (
                <DataState title={t("sections.inbox.p47")} copy={t("sections.inbox.p48")} />
            ) : filteredRows.length === 0 ? (
                <DataState title={t("sections.inbox.p49")} copy={t("sections.inbox.p50")} />
            ) : (
                <div className="inbox-review-table-wrap">
                    <table className="inbox-review-table">
                        <thead><tr><th>{t("sections.inbox.p38")}</th><th>{t("sections.inbox.p51")}</th><th>{t("sections.inbox.p52")}</th><th>{t("sections.inbox.p53")}</th><th>{t("sections.inbox.p54")}</th><th>{t("sections.inbox.p43")}</th><th aria-label={t("sections.inbox.p55")} /></tr></thead>
                        <tbody>{filteredRows.map((item) => (
                            <tr key={`${item.type}-${item.id}`}>
                                <td>{item.typeLabel}</td><td>{item.user || "—"}</td><td className="inbox-review-text">{item.text || "—"}</td><td>{item.score ?? "—"}</td><td>{formatDateTime(item.created_at)}</td>
                                <td><StatusBadge tone={item.is_canceled ? "danger" : "success"}>{item.is_canceled ? i18n.t("sections.inbox.p56") : i18n.t("sections.inbox.p57")}</StatusBadge></td>
                                <td>{!item.is_canceled && capabilities.hasAction("content.manage") ? <button className="inbox-danger-button" type="button" aria-label={i18n.t("sections.inbox.p58", {v0: item.id})} onClick={() => setSelectedReview(item)}>{i18n.t("sections.inbox.p59")}</button> : null}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}
            <ReviewCancelDialog review={selectedReview} mutation={cancelMutation} onClose={() => setSelectedReview(null)} />
        </>
    );
};

export const InboxPage = () => {
    const reviews = useLocation().pathname.startsWith("/reviews");
    const capabilities = useCapabilities();
    const deployer = capabilities.user?.role === ADMIN_ROLES.DEPLOYER
        || capabilities.user?.role === ADMIN_ROLES.IMPLEMENTATION_HEAD;
    return (
        <div className="admin-page inbox-page">
            <InboxHeader reviews={reviews} deployer={deployer} />
            <main className="inbox-workspace">{reviews ? <ReviewQueue /> : <NotificationQueue deployer={deployer} />}</main>
        </div>
    );
};
